"use client"

import * as React from "react"

/**
 * TEMPORARY diagnostic for the fade over the top of the phone header (remove once found). Turned on by tapping
 * the phone menu's title five times; it then lists, front to back, every element at a few points in the faded
 * strip, and what iOS reports about the screen. If one of our elements paints there, it shows up by name.
 */
export const LAYOUT_DEBUG_STORAGE_KEY = "ibada.layoutDebug"
export const LAYOUT_DEBUG_EVENT = "ibada:layout-debug-toggle"

function describe(element: Element): string {
  const id = element.id ? `#${element.id}` : ""
  const className = typeof element.className === "string" ? element.className : element.getAttribute("class") ?? ""
  const classes = className.trim() ? `.${className.trim().split(/\s+/).slice(0, 4).join(".")}` : ""
  const style = getComputedStyle(element)
  const paint = [style.backgroundImage !== "none" ? "bgimg" : "", style.backdropFilter && style.backdropFilter !== "none" ? "backdrop" : "", style.opacity !== "1" ? `op${style.opacity}` : "", style.maskImage && style.maskImage !== "none" ? "mask" : ""]
    .filter(Boolean)
    .join(",")
  return `${element.tagName.toLowerCase()}${id}${classes}`.slice(0, 70) + (paint ? ` [${paint}]` : "")
}

function readSafeArea(side: "top" | "bottom"): string {
  const probe = document.createElement("div")
  probe.style.cssText = `position:fixed;visibility:hidden;padding-${side}:env(safe-area-inset-${side})`
  document.body.appendChild(probe)
  const value = getComputedStyle(probe).getPropertyValue(`padding-${side}`)
  probe.remove()
  return value
}

function snapshot(): string[] {
  const x = Math.round(window.innerWidth / 2)
  const lines: string[] = []
  for (const y of [10, 40, 70, 100]) {
    const stack = document
      .elementsFromPoint(x, y)
      .filter((element) => !element.closest("[data-layout-debug]"))
      .slice(0, 5)
      .map(describe)
    lines.push(`@${y}: ${stack.join("  >  ")}`)
  }
  const viewport = window.visualViewport
  const meta = (name: string) => document.querySelector(`meta[name="${name}"]`)?.getAttribute("content") ?? "(none)"
  lines.push(
    `inner ${window.innerWidth}x${window.innerHeight}  screen ${screen.width}x${screen.height}  vv ${viewport ? `${Math.round(viewport.height)} @${Math.round(viewport.offsetTop)}` : "-"}`,
    `scrollY ${Math.round(window.scrollY)}  doc ${document.documentElement.scrollHeight}/${document.documentElement.clientHeight}  safe ${readSafeArea("top")}/${readSafeArea("bottom")}`,
    `--app-height ${getComputedStyle(document.documentElement).getPropertyValue("--app-height").trim()}  standalone ${String((navigator as Navigator & { standalone?: boolean }).standalone ?? matchMedia("(display-mode: standalone)").matches)}`,
    `theme-color ${meta("theme-color")}  status-bar ${meta("apple-mobile-web-app-status-bar-style")}  html-bg ${getComputedStyle(document.documentElement).backgroundColor}`,
  )
  return lines
}

export function LayoutDebugPanel() {
  const [enabled, setEnabled] = React.useState(() => {
    try {
      return window.localStorage.getItem(LAYOUT_DEBUG_STORAGE_KEY) === "1"
    } catch {
      return false
    }
  })
  const [lines, setLines] = React.useState<string[]>([])

  React.useEffect(() => {
    const onToggle = () => setEnabled((value) => !value)
    window.addEventListener(LAYOUT_DEBUG_EVENT, onToggle)
    return () => window.removeEventListener(LAYOUT_DEBUG_EVENT, onToggle)
  }, [])

  React.useEffect(() => {
    try {
      if (enabled) window.localStorage.setItem(LAYOUT_DEBUG_STORAGE_KEY, "1")
      else window.localStorage.removeItem(LAYOUT_DEBUG_STORAGE_KEY)
    } catch {
      // Storage can be unavailable; the panel still works for this session.
    }
    if (!enabled) return
    const refresh = () => setLines(snapshot())
    refresh()
    const id = window.setInterval(refresh, 1500)
    return () => window.clearInterval(id)
  }, [enabled])

  if (!enabled) return null
  return (
    <div
      data-layout-debug=''
      className='fixed inset-x-2 top-1/3 z-[2147483646] max-h-[45vh] overflow-y-auto rounded-xl bg-black/85 p-2.5 font-mono text-[10px] leading-snug text-white'
    >
      {lines.map((line) => (
        <p key={line} className='break-all'>
          {line}
        </p>
      ))}
      <div className='mt-2 flex gap-2'>
        <button
          type='button'
          className='rounded-full bg-white/20 px-3 py-1.5 font-sans text-xs font-bold'
          onClick={() => {
            document.querySelector('meta[name="theme-color"]')?.remove()
            setLines(snapshot())
          }}
        >
          Remove theme-color
        </button>
        <button type='button' className='rounded-full bg-white/20 px-3 py-1.5 font-sans text-xs font-bold' onClick={() => setEnabled(false)}>
          Close
        </button>
      </div>
    </div>
  )
}
